import { Component, OnInit } from '@angular/core';
import { LoaderService } from '../sharedcomponents/index';
import { BookingService } from '../appservices/booking.service';

// Fields that can be updated via CSV (display label + CSV column name)
export const BULK_FIELDS = [
  { key: 'discountsApplicable.priceperday', label: 'Discount Price/Day' },
  { key: 'discountsApplicable.validTillDate', label: 'Discount Valid Till (epoch)' },
  { key: 'gofRevenueShare', label: 'GoF Revenue Share' },
  { key: 'operatorName', label: 'Operator Name' },
  { key: 'isApproved', label: 'Is Approved' },
  { key: 'managerEmail', label: 'Manager Email' },
  { key: 'managerName', label: 'Manager Name' },
  { key: 'managerphone', label: 'Manager Phone' },
  { key: 'mobile', label: 'Mobile' },
  { key: 'isArchived', label: 'Is Archived' },
  { key: 'online', label: 'Online' },
  { key: 'ownerEmail', label: 'Owner Email' },
  { key: 'ownerName', label: 'Owner Name' },
  { key: 'priceperhr', label: 'Price/Hour' },
  { key: 'priceperday', label: 'Price/Day' },
  { key: 'pricepermonth', label: 'Price/Month' },
  { key: 'seatsAvailable', label: 'Seats Available' },
  { key: 'isInstantlyBookableWeWork', label: 'Instantly Bookable (WeWork)' },
  { key: 'isInstantlyBookableAwfis', label: 'Instantly Bookable (Awfis)' },
  { key: 'isInstantlyBookableInnov8', label: 'Instantly Bookable (Innov8)' },
  { key: 'isInstantlyBookableCreflo', label: 'Instantly Bookable (Creflo)' },
  { key: 'weworkBuildingId', label: 'WeWork Building ID' },
  { key: 'weworkInventoryId', label: 'WeWork Inventory ID' },
  { key: 'awfisBuildingId', label: 'Awfis Building ID' },
  { key: 'awfisInventoryId', label: 'Awfis Inventory ID' },
  { key: 'innov8BuildingId', label: 'Innov8 Building ID' },
  { key: 'innov8InventoryId', label: 'Innov8 Inventory ID' },
  { key: 'crefloBuildingId', label: 'Creflo Building ID' },
  { key: 'crefloInventoryId', label: 'Creflo Inventory ID' },
];

export interface SpaceRow {
  spaceId: string;
  newValues: { [key: string]: any };
  currentValues?: { [key: string]: any };
  spaceName?: string;
  status?: 'pending' | 'success' | 'error' | 'skipped';
  resultMessage?: string;
}

@Component({
  selector: 'app-space-bulk-update-new',
  templateUrl: './space-bulk-update-new.component.html',
  styleUrls: ['./space-bulk-update-new.component.scss']
})
export class SpaceBulkUpdateNewComponent implements OnInit {

  step: 'upload' | 'preview' | 'done' = 'upload';

  // CSV parse results
  csvHeaders: string[] = [];
  rows: SpaceRow[] = [];
  activeFields: string[] = [];   // field keys actually present in the CSV

  errorMessage: string = '';
  fetchError: string = '';
  updateSummary: { success: number; error: number; skipped: number } = { success: 0, error: 0, skipped: 0 };

  readonly BULK_FIELDS = BULK_FIELDS;

  constructor(
    private loader: LoaderService,
    private bookingService: BookingService
  ) {}

  ngOnInit() {}

  // ── Step 1: CSV upload ──────────────────────────────────────────────────────

  onFileSelected(event: any) {
    const file: File = event.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e: any) => {
      this.parseCSV(e.target.result);
    };
    reader.readAsText(file);
  }

  parseCSV(text: string) {
    this.errorMessage = '';
    this.rows = [];
    this.csvHeaders = [];
    this.activeFields = [];

    const lines = text.split(/\r?\n/).filter(l => l.trim());
    if (lines.length < 2) {
      this.errorMessage = 'CSV must have a header row and at least one data row.';
      return;
    }

    const headers = lines[0].split(',').map(h => h.trim());
    if (!headers.includes('spaceId')) {
      this.errorMessage = 'CSV must include a "spaceId" column.';
      return;
    }

    this.csvHeaders = headers;
    // Which BULK_FIELDS keys are present in the CSV?
    this.activeFields = BULK_FIELDS.map(f => f.key).filter(k => headers.includes(k));

    for (let i = 1; i < lines.length; i++) {
      const cols = this.splitCSVLine(lines[i]);
      if (cols.length < headers.length) continue;
      const row: SpaceRow = { spaceId: '', newValues: {}, status: 'pending' };
      headers.forEach((h, idx) => {
        if (h === 'spaceId') {
          row.spaceId = cols[idx].trim();
        } else if (this.activeFields.includes(h)) {
          row.newValues[h] = cols[idx].trim();
        }
      });
      if (row.spaceId) this.rows.push(row);
    }

    if (this.rows.length === 0) {
      this.errorMessage = 'No valid rows found in CSV.';
    }
  }

  // Simple CSV line split (handles quoted fields)
  splitCSVLine(line: string): string[] {
    const result = [];
    let cur = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') { inQuotes = !inQuotes; }
      else if (ch === ',' && !inQuotes) { result.push(cur); cur = ''; }
      else { cur += ch; }
    }
    result.push(cur);
    return result;
  }

  fieldLabel(key: string): string {
    const f = BULK_FIELDS.find(b => b.key === key);
    return f ? f.label : key;
  }

  // ── Step 2: Fetch current values & show preview ──────────────────────────────

  async fetchAndPreview() {
    this.fetchError = '';
    this.loader.display(true);
    try {
      const spaceIds = this.rows.map(r => r.spaceId);
      const res: any = await this.bookingService.bulkUpdateNewPreview(spaceIds);
      if (res && res.spaces) {
        this.rows.forEach(row => {
          const current = res.spaces[row.spaceId];
          if (current) {
            row.currentValues = current;
            row.spaceName = current.spaceName || row.spaceId;
          } else {
            row.currentValues = null;
            row.spaceName = '⚠ Not found';
          }
        });
      }
      this.step = 'preview';
    } catch (err) {
      this.fetchError = 'Failed to fetch space details. Please try again.';
      console.error(err);
    } finally {
      this.loader.display(false);
    }
  }

  hasChange(row: SpaceRow, field: string): boolean {
    if (!row.currentValues) return false;
    const newVal = row.newValues[field];
    if (newVal === '' || newVal === null || newVal === undefined) return false;
    const oldVal = row.currentValues[field];
    return this.normaliseForCompare(oldVal) !== this.normaliseForCompare(newVal);
  }

  private normaliseForCompare(val: any): string {
    if (val === null || val === undefined) return '';
    const s = String(val).trim().toLowerCase();
    if (s === 'true')  return 'true';
    if (s === 'false') return 'false';
    return s;
  }

  // ── Step 3: Apply updates ────────────────────────────────────────────────────

  async applyUpdates() {
    this.loader.display(true);
    try {
      const updates = this.rows
        .filter(r => r.currentValues !== null)
        .map(r => ({ spaceId: r.spaceId, fields: r.newValues }));

      const res: any = await this.bookingService.bulkUpdateNew(updates);
      this.updateSummary = { success: 0, error: 0, skipped: 0 };

      if (res && res.results) {
        res.results.forEach((result: any) => {
          const row = this.rows.find(r => r.spaceId === result.spaceId);
          if (row) {
            row.status = result.status === 'success' ? 'success'
                       : result.status === 'skipped' ? 'skipped' : 'error';
            row.resultMessage = result.status === 'success'
              ? result.changes ? result.changes.join('; ') : 'Updated'
              : result.message || 'Error';
            this.updateSummary[row.status]++;
          }
        });
      }
      this.step = 'done';
    } catch (err) {
      console.error(err);
      this.fetchError = 'Update failed. Please try again.';
    } finally {
      this.loader.display(false);
    }
  }

  reset() {
    this.step = 'upload';
    this.rows = [];
    this.csvHeaders = [];
    this.activeFields = [];
    this.errorMessage = '';
    this.fetchError = '';
  }

  downloadSampleCSV() {
    const headers = ['spaceId', ...BULK_FIELDS.map(f => f.key)];
    const sample = ['-My6C7QVxkyghnOIepIv', '950', '1790792999999', '20', 'true',
      'manager@example.com', 'Manager Name', '9999999999', '9999999999', 'true',
      'owner@example.com', 'Owner Name', '500', '950', '', '100', 'true', 'false', 'false', 'false',
      'DEL04', '', '', '', '', '', '', ''];
    const csv = headers.join(',') + '\n' + sample.join(',');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'bulk_update_template.csv'; a.click();
    URL.revokeObjectURL(url);
  }
}
